import * as React from 'react'
import { useCallback, useContext, useMemo } from 'react'

import {
  flightsBusinessFareClassSelect,
  flightsEconomyFareClassSelect,
  flightsFirstFareClassSelect,
  flightsOneWayFareTypeSelect,
  flightsPremiumFareClassSelect,
  flightsReturnFareTypeSelect,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import FareDropdown from 'components/Flights/FlightsSearch/SearchWidget/FareDropdown/FareDropdown'
import FareRadio from 'components/Flights/FlightsSearch/SearchWidget/FareDropdown/FareRadio'
import FlightSearchWidgetDesktopMultiCityForm from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetDesktopMultiCityForm'
import FlightSearchTravellerSelect from 'components/Flights/FlightsSearch/SearchWidget/FlightTravellerSelect/FlightSearchTravellerSelect'
import type { FlightDropdownOption } from 'components/Flights/types'
import Group from 'components/utils/Group'
import { FlightsClassTypes, FlightsFareTypes } from 'constants/flight'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import {
  createEmptyFlightSearchWidgetFlightItem,
  FlightSearchWidgetActions,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type {
  FlightSearchWidgetFlightItem,
  FlightSearchWidgetInitialFocus,
} from 'contexts/Flights/FlightSearchWidget/types'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import type { TripStop } from 'tripPlanner/api/types'

import TripContextFlightSearchForm from './TripContextFlightSearchForm'

interface Props {
  onSearchAirport: () => void
  readOnlyAirports?: boolean
  showMultiCity?: boolean
  autoFocusArrival?: boolean
  wrapControls?: boolean
  groupAirportDropdowns?: boolean
  portaledDropdowns?: boolean
  initialFocus?: FlightSearchWidgetInitialFocus
  /** The trip's stops, drawn behind the date calendar as read-only context. */
  tripStops?: ReadonlyArray<TripStop>
  /** The stop this flight arrives at, so the calendar opens on its month. */
  contextStopId?: number
}

/**
 * The desktop flight search widget, with the trip behind its date calendar.
 *
 * A trip-planner-owned copy of the standard widget's composition: the fare
 * radio, traveller select, cabin dropdown and the whole multi-city form are
 * the shared components untouched. Only the single-trip form is ours, and only
 * so its date sheet can draw the itinerary.
 *
 * Multi-city deliberately keeps the standard calendar — its legs are quoted
 * per search rather than per day, so there is no per-day fare to preserve and
 * no reason to fork it.
 */
function TripContextFlightSearchWidget(props: Props) {
  const {
    onSearchAirport,
    readOnlyAirports,
    showMultiCity,
    autoFocusArrival,
    wrapControls,
    groupAirportDropdowns,
    portaledDropdowns,
    initialFocus,
    tripStops,
    contextStopId,
  } = props
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const { fareTypes, fareCabins } = useFlightFareLabels()

  const { fareType, fareCabin } = state

  const changeFareType = useCallback(
    (fareType: FlightDropdownOption) => {
      dispatch({ type: FlightSearchWidgetActions.SET_FARE_TYPE, fareType })

      if (fareType.value === FlightsFareTypes.ONE_WAY) {
        fireInteractionEvent(flightsOneWayFareTypeSelect())
      } else if (fareType.value === FlightsFareTypes.RETURN) {
        fireInteractionEvent(flightsReturnFareTypeSelect())
      }

      // Multi-city and one-way have no return leg to keep.
      if ([FlightsFareTypes.MULTI_CITY, FlightsFareTypes.ONE_WAY].includes(fareType.value as FlightsFareTypes)) {
        dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id: state.flights[0].id, date: null })
      }

      // One-way and return are a single leg.
      if ([FlightsFareTypes.ONE_WAY, FlightsFareTypes.RETURN].includes(fareType.value as FlightsFareTypes)) {
        for (let i = 1; i < state.flights.length; i++) {
          dispatch({ type: FlightSearchWidgetActions.REMOVE_FLIGHT, payload: state.flights[i].id })
        }
      }

      // Multi-city starts with two.
      if (fareType.value === FlightsFareTypes.MULTI_CITY && state.flights.length === 1) {
        const item: FlightSearchWidgetFlightItem = createEmptyFlightSearchWidgetFlightItem()
        dispatch({ type: FlightSearchWidgetActions.ADD_FLIGHT, payload: item })
      }
    },
    [dispatch, state.flights],
  )

  const changeFareCabin = useCallback(
    (fareCabin: FlightDropdownOption) => {
      dispatch({ type: FlightSearchWidgetActions.SET_FARE_CABIN, fareCabin })
      switch (fareCabin.value) {
        case FlightsClassTypes.ECONOMY:
          fireInteractionEvent(flightsEconomyFareClassSelect())
          break
        case FlightsClassTypes.PREMIUM:
          fireInteractionEvent(flightsPremiumFareClassSelect())
          break
        case FlightsClassTypes.BUSINESS:
          fireInteractionEvent(flightsBusinessFareClassSelect())
          break
        case FlightsClassTypes.FIRST:
          fireInteractionEvent(flightsFirstFareClassSelect())
          break
        default:
          break
      }
    },
    [dispatch],
  )

  const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSearchAirport()
  }, [onSearchAirport])

  const isMultiCity = fareType?.value === FlightsFareTypes.MULTI_CITY

  const fareOptions = useMemo(() => {
    if (!showMultiCity) {
      return fareTypes.filter((fare) => fare.value !== FlightsFareTypes.MULTI_CITY)
    }
    return fareTypes
  }, [showMultiCity, fareTypes])

  const travellerAndCabinControls = <>
    <FlightSearchTravellerSelect />

    <FareDropdown
      options={fareCabins}
      selectedOption={fareCabin}
      onOptionChange={changeFareCabin}
      data-testid="fare-class-dropdown"
    />
  </>

  return (
    <Group direction="vertical" gap={16}>
      <Group
        direction="horizontal"
        gap={wrapControls ? '12 24' : 32}
        wrap={wrapControls ? 'wrap' : undefined}
      >
        <FareRadio
          options={fareOptions}
          selectedOption={fareType}
          onOptionChange={changeFareType}
          flattenItems={wrapControls}
          data-testid="fare-type-dropdown"
        />

        {wrapControls ? travellerAndCabinControls : (
          <Group direction="horizontal" gap={12}>
            {travellerAndCabinControls}
          </Group>
        )}
      </Group>

      {!isMultiCity && (
        <TripContextFlightSearchForm
          onSubmit={onSubmit}
          readOnlyAirports={readOnlyAirports}
          autoFocusArrival={autoFocusArrival}
          portaledDropdowns={portaledDropdowns}
          groupAirportDropdowns={groupAirportDropdowns}
          initialFocus={initialFocus}
          tripStops={tripStops}
          contextStopId={contextStopId}
        />
      )}

      {isMultiCity && (
        <FlightSearchWidgetDesktopMultiCityForm
          onSubmit={onSubmit}
          readOnlyAirports={readOnlyAirports}
          groupAirportDropdowns={groupAirportDropdowns}
          initialFocus={initialFocus}
        />
      )}
    </Group>
  )
}

export default TripContextFlightSearchWidget
